import React, { useState } from 'react';
import { ShieldAlertIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { naira } from '../../utils/format';
import { academicServices, serviceProviders, servicesAdmin } from '../../data/services';
import { ORIGIN_LABEL, ORIGIN_TONE } from '../../utils/services';
const TABS = ['Overview', 'Catalogue', 'Providers', 'Disputes', 'Integrity', 'Audit'] as const;
type Tab = (typeof TABS)[number];

/**
 * The Academic Services control centre. Kept separate from general
 * administration, moderation and financial operations.
 */
export function ServicesControl() {
  const [tab, setTab] = useState<Tab>('Overview');
  return <div className="space-y-6" data-id="element-6590" data-mp="29zqrdcepdrqk">
      <PageHeader eyebrow="Campuzon administration" title="Academic Services control centre" description="Catalogues, providers, verification, disputes and academic-integrity enforcement. Every action here is attributed and logged." data-id="element-6591" data-mp="2a0sewgcwo6p6" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Services administration sections" data-id="element-6592" data-mp="29bs0v40ka1o1" />

      {tab === 'Overview' && <>
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-6593" data-mp="24k9ws738rkyu">
            {servicesAdmin.metrics.map(metric => <li key={metric.label} className="rounded-card border border-line bg-surface px-4 py-3.5" data-id="element-6594" data-mp="2j84szxggk7go">
                <p className="text-[12px] font-medium text-ink-muted" data-id="element-6595" data-mp="2ecz4qrzl2gpf">{metric.label}</p>
                <p className="cz-num mt-1 text-2xl font-semibold tracking-tight text-ink" data-id="element-6596" data-mp="2s9urfn8rc6ny">
                  {metric.value}
                </p>
                <p className="mt-0.5 text-[12px] text-ink-subtle" data-id="element-6597" data-mp="xwd5g4o03hv1">{metric.note}</p>
              </li>)}
          </ul>

          <Panel title="Boundaries this module enforces" description="Non-negotiable, and visible to administrators as policy rather than convention." data-id="element-6598" data-mp="twcooddfiukt">
            <ul className="grid gap-2 sm:grid-cols-2" data-id="element-6599" data-mp="2xy2x4ahditzf">
              {['A third-party provider is never presented as the university.', 'Campuzon never issues, reprints or certifies an academic document.', 'Unpublished official fees and timelines read as unavailable, never estimated.', 'Credential sharing is prohibited and enforced by suspension.', 'Confidential references are inaccessible to the student at every stage.', 'A generated authorisation is not claimed as institutionally accepted unless confirmed.'].map(rule => <li key={rule} className="flex items-start gap-2 text-[12px] leading-5 text-ink-muted" data-id="element-6600" data-mp="3qpmfxta2660w">
                  <ShieldAlertIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warn-700" data-id="element-6601" data-mp="3vktfcowaook0" />
                  {rule}
                </li>)}
            </ul>
          </Panel>
        </>}

      {tab === 'Catalogue' && <Panel title="Service catalogue" description="Per institution. Adding a university means adding a catalogue, not changing the platform." data-id="element-6602" data-mp="1jz1dcdidmw0q">
          <ul className="divide-y divide-line" data-id="element-6603" data-mp="1a13hb3u9zuxk">
            {academicServices.map(service => <li key={service.id} className="flex flex-wrap items-center gap-2 py-3 first:pt-0" data-id="element-6604" data-mp="27ovlwivnxyf6">
                <span className="min-w-0 flex-1" data-id="element-6605" data-mp="22psmbdygckel">
                  <span className="block text-[13px] font-medium text-ink" data-id="element-6606" data-mp="22qaoer7spsuf">{service.name}</span>
                  <span className="block text-[12px] text-ink-muted" data-id="element-6607" data-mp="1xxr7pldtahle">
                    {service.institutionId.toUpperCase()} · {service.category} · updated{' '}
                    {service.lastUpdated}
                  </span>
                </span>
                <Badge tone={ORIGIN_TONE[service.origin]} data-id="element-6608" data-mp="2lowqo9f1tpzx">{ORIGIN_LABEL[service.origin]}</Badge>
                <Badge tone={service.catalogueVerified ? 'ok' : 'warn'} data-id="element-6609" data-mp="266n3kokoqqtd">
                  {service.catalogueVerified ? 'Confirmed with institution' : 'Unconfirmed'}
                </Badge>
              </li>)}
          </ul>
        </Panel>}

      {tab === 'Providers' && <div className="space-y-4" data-id="element-6610" data-mp="217isfse7aj0e">
          <Panel title="Verification queue" description="No provider appears as Campuzon Verified until every item clears." data-id="element-6611" data-mp="218lhduryqbdo">
            <ul className="space-y-3" data-id="element-6612" data-mp="2tw7vhwytgyes">
              {servicesAdmin.verificationQueue.map(item => <li key={item.id} className="rounded-card border border-line-strong bg-surface px-4 py-3.5" data-id="element-6613" data-mp="2f7bidlhwkv7b">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-6614" data-mp="2k5v2dgcxrwx1">
                    <p className="text-[13px] font-semibold text-ink" data-id="element-6615" data-mp="2k41sztaxuylc">{item.name}</p>
                    <Badge tone="warn" data-id="element-6616" data-mp="37unz3f1du8sp">Pending verification</Badge>
                    <span className="cz-num ml-auto text-[12px] text-ink-subtle" data-id="element-6617" data-mp="3cnqw5x5dc0dn">
                      {item.id} · {item.submitted}
                    </span>
                  </div>
                  <ul className="mt-2 space-y-1" data-id="element-6618" data-mp="w9f61uv4zubb">
                    {item.missing.map(missing => <li key={missing} className="text-[12px] leading-5 text-ink-muted" data-id="element-6619" data-mp="1a0a7im3b394v">
                        · {missing}
                      </li>)}
                  </ul>
                  <div className="mt-2.5 flex flex-wrap gap-2" data-id="element-6620" data-mp="i82rnnvmk0rj">
                    <Button size="sm" variant="secondary" data-id="element-6621" data-mp="d9rg98poli77">
                      Request documents
                    </Button>
                    <Button size="sm" variant="primary" disabled data-id="element-6622" data-mp="r9sstlfb6e2i">
                      Approve — blocked until items clear
                    </Button>
                  </div>
                </li>)}
            </ul>
          </Panel>

          <Panel title="Provider register" data-id="element-6623" data-mp="22muznorlvp9h">
            <ul className="divide-y divide-line" data-id="element-6624" data-mp="1xq4hmvw8qjlf">
              {serviceProviders.map(provider => <li key={provider.id} className="flex flex-wrap items-center gap-2 py-3 first:pt-0" data-id="element-6625" data-mp="vcq8wjreuxe9">
                  <span className="min-w-0 flex-1" data-id="element-6626" data-mp="10ai9e264fru2">
                    <span className="block text-[13px] font-medium text-ink" data-id="element-6627" data-mp="10de65n68gagx">{provider.name}</span>
                    <span className="block text-[12px] text-ink-muted" data-id="element-6628" data-mp="14asb32mvrw34">
                      {provider.location} · {provider.completed} completed · {provider.disputes} dispute
                      {provider.disputes === 1 ? '' : 's'}
                    </span>
                  </span>
                  <Badge tone={provider.status === 'Verified' ? 'ok' : 'warn'} data-id="element-6629" data-mp="gnk6yo16ut2o">{provider.status}</Badge>
                </li>)}
            </ul>
          </Panel>
        </div>}

      {tab === 'Disputes' && <Panel title="Open disputes" description="Provider funds stay held until a dispute resolves." data-id="element-6630" data-mp="3p33ynim7o3fm">
          <ul className="space-y-3" data-id="element-6631" data-mp="3u273b1ya47n2">
            {servicesAdmin.disputes.map(dispute => <li key={dispute.id} className="rounded-card border border-line-strong bg-surface px-4 py-3.5" data-id="element-6632" data-mp="3mio1zfl63rpo">
                <div className="flex flex-wrap items-center gap-2" data-id="element-6633" data-mp="3hjup9pdpg4qk">
                  <p className="text-[13px] font-semibold text-ink" data-id="element-6634" data-mp="3hhi7pdbuzwe1">{dispute.reason}</p>
                  <Badge tone="warn" data-id="element-6635" data-mp="3ciovdo8b6bfg">Raised by {dispute.raisedBy.toLowerCase()}</Badge>
                  <span className="cz-num ml-auto text-[12px] text-ink-subtle" data-id="element-6636" data-mp="38idacac1a93g">
                    {dispute.id} · {dispute.requestId}
                  </span>
                </div>
                <p className="mt-1 text-[13px] text-ink-muted" data-id="element-6637" data-mp="2ys25mlkizvvi">{dispute.state}</p>
                <p className="cz-num mt-1 text-[12px] text-ink-subtle" data-id="element-6638" data-mp="2tth66od3lg84">
                  {naira(dispute.escrow)} held pending resolution
                </p>
              </li>)}
          </ul>
        </Panel>}

      {tab === 'Integrity' && <Panel title="Academic integrity flags" description="Impersonation, credential requests and misuse of institutional branding." data-id="element-6639" data-mp="2kvf0hvif5zei">
          <ul className="space-y-3" data-id="element-6640" data-mp="7muhuv9pykrl">
            {servicesAdmin.integrityFlags.map(flag => <li key={flag.id} className="rounded-card border border-line-strong bg-surface px-4 py-3.5" data-id="element-6641" data-mp="cjb7xv0hb97b">
                <div className="flex flex-wrap items-center gap-2" data-id="element-6642" data-mp="3uppgtfjw4syu">
                  <Badge tone={flag.severity === 'High' ? 'danger' : 'warn'} data-id="element-6643" data-mp="2lomsqsl6lew">{flag.severity}</Badge>
                  <span className="cz-num ml-auto text-[12px] text-ink-subtle" data-id="element-6644" data-mp="3l05ws6kfyt2e">{flag.id}</span>
                </div>
                <p className="mt-1.5 text-[13px] leading-6 text-ink" data-id="element-6645" data-mp="3prxsevigfoze">{flag.detail}</p>
                <p className="mt-1 text-[12px] leading-5 text-ink-muted" data-id="element-6646" data-mp="3b31ckca5p7o8">{flag.action}</p>
              </li>)}
          </ul>
        </Panel>}

      {tab === 'Audit' && <Panel title="Audit log" description="Every sensitive administrative action, attributed." data-id="element-6647" data-mp="3211v5iv8c1kr">
          <ul className="divide-y divide-line" data-id="element-6648" data-mp="370xwfacjlewm">
            {servicesAdmin.auditLog.map(entry => <li key={entry.at} className="py-2.5 first:pt-0" data-id="element-6649" data-mp="2ne34hq475b73">
                <p className="text-[13px] text-ink" data-id="element-6650" data-mp="2ifrt3ay96smr">{entry.action}</p>
                <p className="cz-num text-[12px] text-ink-subtle" data-id="element-6651" data-mp="2ihmfyut34uth">
                  {entry.at} · {entry.actor}
                </p>
              </li>)}
          </ul>
        </Panel>}
    </div>;
}